import { DeleteOutlined, EditOutlined, ShopOutlined } from '@ant-design/icons';
import { useStyleRegister } from '@ant-design/cssinjs';
import { Button, Card, Flex, Popconfirm, Space, Table, Tag, theme, Typography } from 'antd';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { buildVisibleOperationEntityTree } from '../utils/operation-entities';
import {
  formatOperationEntityPrincipalNames,
  formatRecommendedContact,
  getRecommendationIssue,
  mergeOperationEntityPrincipals,
} from '../utils/operation-entity-principals';
import OperationEntityBatchMoveForm from './OperationEntityBatchMoveForm';
import OperationEntityToolbar from './OperationEntityToolbar';

const TABLE_CLASS_NAME = 'operation-entity-principal-table';

const getEntityType = (entity) => {
  if (!entity.parentId) {
    return '总部';
  }
  return entity.isStore ? '门店' : '区域';
};

const OperationEntityTable = ({
  allEntities,
  entities,
  notice,
  onBatchUpdate,
  onCreate,
  onDelete,
  onEdit,
  recommendation,
}) => {
  const { hashId, theme: designTheme, token } = theme.useToken();
  useStyleRegister(
    {
      path: ['OperationEntityTable', hashId],
      theme: designTheme,
      token,
    },
    () => ({
      [`.${TABLE_CLASS_NAME} .operation-entity-recommendation-conflict > td`]: {
        backgroundColor: token.colorWarningBg,
      },
      [`.${TABLE_CLASS_NAME} .operation-entity-recommendation-conflict:hover > td`]: {
        backgroundColor: token.colorWarningBg,
      },
      [`.${TABLE_CLASS_NAME} .operation-entity-recommendation-unmatched > td`]: {
        backgroundColor: token.colorErrorBg,
      },
      [`.${TABLE_CLASS_NAME} .operation-entity-recommendation-unmatched:hover > td`]: {
        backgroundColor: token.colorErrorBg,
      },
    }),
  );
  const [moving, setMoving] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const dataSource = useMemo(() => buildVisibleOperationEntityTree(entities), [entities]);
  const visibleEntityIds = useMemo(
    () => new Set(entities.filter((entity) => entity.parentId).map((entity) => entity.id)),
    [entities],
  );
  const selectedCount = selectedRowKeys.length;

  useEffect(() => {
    setSelectedRowKeys((current) => {
      const visibleKeys = current.filter((entityId) => visibleEntityIds.has(entityId));
      return visibleKeys.length === current.length ? current : visibleKeys;
    });
  }, [visibleEntityIds]);

  const submitBatch = async (data) => {
    const success = await onBatchUpdate(data);
    if (success) {
      setSelectedRowKeys([]);
      setMoving(false);
    }
    return success;
  };

  const renderEffectivePrincipals = (principals) => {
    const mergedPrincipals = mergeOperationEntityPrincipals(principals);
    if (!mergedPrincipals.length) {
      return '-';
    }
    return (
      <Flex vertical gap="small">
        {mergedPrincipals.map((principal) => (
          <Space key={principal.memberId || principal.id} size="small" wrap>
            <Typography.Text>
              {principal.name || principal.phoneNumber || principal.memberId || principal.id}
            </Typography.Text>
          </Space>
        ))}
      </Flex>
    );
  };

  const renderPrincipals = (principals, entity) => {
    const effectivePrincipals = renderEffectivePrincipals(principals);
    const item = recommendation.itemByEntityId[entity.id];
    const showRecommendation = entity.parentId && item && recommendation.matching;
    if (!showRecommendation) {
      return effectivePrincipals;
    }
    const issue = getRecommendationIssue(item);
    return (
      <Flex vertical gap="small">
        <Typography.Text>当前：{formatOperationEntityPrincipalNames(principals)}</Typography.Text>
        {issue ? (
          <Space size="small" wrap>
            <Tag color={issue === 'conflict' ? 'warning' : 'error'}>
              {issue === 'conflict' ? '冲突' : '未匹配'}
            </Tag>
            <Typography.Text type="secondary">{item.reason}</Typography.Text>
          </Space>
        ) : (
          <Typography.Text>
            推荐：{formatRecommendedContact(item.recommendedContact, item.departmentPath)}
          </Typography.Text>
        )}
      </Flex>
    );
  };

  const renderActions = (entity) => (
    <Space size="small">
      <Button type="link" size="small" icon={<EditOutlined />} onClick={() => onEdit(entity)}>
        编辑
      </Button>
      {!entity.isStore ? (
        <Popconfirm
          title="删除区域实体"
          description="删除后无法恢复，确定继续吗？"
          okText="删除"
          okButtonProps={{ danger: true }}
          cancelText="取消"
          onConfirm={() => onDelete(entity)}
        >
          <Button danger type="link" size="small" icon={<DeleteOutlined />}>
            删除
          </Button>
        </Popconfirm>
      ) : null}
    </Space>
  );

  const columns = [
    {
      title: '组织名称',
      dataIndex: 'name',
      render: (name, entity) => (
        <Space size="small">
          {entity.isStore ? <ShopOutlined /> : null}
          <Typography.Text strong>{name}</Typography.Text>
        </Space>
      ),
    },
    {
      title: '类型',
      key: 'entityType',
      width: 100,
      render: (_, entity) => getEntityType(entity),
    },
    {
      title: '营业状态',
      dataIndex: ['store', 'businessStatus'],
      width: 110,
      render: (businessStatus) => businessStatus || '-',
    },
    {
      title: '负责人',
      dataIndex: 'principals',
      width: 440,
      render: renderPrincipals,
    },
    {
      title: '启用状态',
      dataIndex: 'enabled',
      width: 90,
      render: (enabled) => (
        <Tag color={enabled ? 'success' : 'default'}>{enabled ? '启用' : '停用'}</Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 150,
      render: (_, entity) => renderActions(entity),
    },
  ];

  return (
    <>
      <Card
        title="组织结构"
        variant="borderless"
        extra={
          <OperationEntityToolbar
            {...recommendation.toolbar}
            onBatchSetEnabled={(enabled) =>
              submitBatch({ action: 'set-enabled', enabled, entityIds: selectedRowKeys })
            }
            onCreate={onCreate}
            onMove={() => setMoving(true)}
            selectedCount={selectedCount}
          />
        }
      >
        <Flex vertical gap="middle">
          {notice}
          <Table
            className={TABLE_CLASS_NAME}
            key={entities
              .map((entity) => `${entity.id}:${entity.parentId || 'root'}:${entity.updatedAt}`)
              .join('|')}
            columns={columns}
            dataSource={dataSource}
            expandable={{ defaultExpandAllRows: true, indentSize: 24 }}
            pagination={false}
            rowClassName={(entity) => {
              if (!recommendation.matching) {
                return '';
              }
              const issue = getRecommendationIssue(recommendation.itemByEntityId[entity.id]);
              return issue ? `operation-entity-recommendation-${issue}` : '';
            }}
            rowKey="id"
            rowSelection={{
              checkStrictly: true,
              getCheckboxProps: (entity) => ({ disabled: !entity.isStore }),
              onChange: setSelectedRowKeys,
              selectedRowKeys,
            }}
            scroll={{ x: 900 }}
          />
        </Flex>
      </Card>
      <OperationEntityBatchMoveForm
        entities={allEntities}
        entityIds={selectedRowKeys}
        open={moving}
        onFinish={submitBatch}
        onOpenChange={setMoving}
      />
    </>
  );
};

OperationEntityTable.propTypes = {
  allEntities: PropTypes.arrayOf(
    PropTypes.shape({
      enabled: PropTypes.bool.isRequired,
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      parentId: PropTypes.string,
    }),
  ).isRequired,
  entities: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      enabled: PropTypes.bool.isRequired,
      id: PropTypes.string.isRequired,
      isStore: PropTypes.bool,
      name: PropTypes.string.isRequired,
      parentId: PropTypes.string,
      principals: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          memberId: PropTypes.string,
          name: PropTypes.string,
          phoneNumber: PropTypes.string,
        }),
      ),
      store: PropTypes.shape({
        businessStatus: PropTypes.string,
        code: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
      }),
      updatedAt: PropTypes.number,
    }),
  ).isRequired,
  notice: PropTypes.node,
  onBatchUpdate: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  recommendation: PropTypes.shape({
    itemByEntityId: PropTypes.objectOf(
      PropTypes.shape({
        currentContacts: PropTypes.arrayOf(
          PropTypes.shape({
            departmentPaths: PropTypes.arrayOf(PropTypes.string),
            externalUserId: PropTypes.string,
            name: PropTypes.string,
          }),
        ),
        departmentPath: PropTypes.string,
        recommendedContact: PropTypes.shape({
          departmentPaths: PropTypes.arrayOf(PropTypes.string),
          externalUserId: PropTypes.string.isRequired,
          name: PropTypes.string,
        }),
        operationEntityId: PropTypes.string.isRequired,
        reason: PropTypes.string,
        status: PropTypes.string.isRequired,
      }),
    ).isRequired,
    matching: PropTypes.bool.isRequired,
    toolbar: PropTypes.shape({
      applicableCount: PropTypes.number.isRequired,
      confirming: PropTypes.bool.isRequired,
      matching: PropTypes.bool.isRequired,
      onCancelMatching: PropTypes.func.isRequired,
      onAppendMatching: PropTypes.func.isRequired,
      onRecommendationFilterChange: PropTypes.func.isRequired,
      onStartMatching: PropTypes.func.isRequired,
      recommendationAvailable: PropTypes.bool.isRequired,
      recommendationFilter: PropTypes.oneOf(['all', 'conflict', 'unmatched']).isRequired,
      recommendationRefreshing: PropTypes.bool.isRequired,
    }).isRequired,
  }).isRequired,
};

OperationEntityTable.defaultProps = {
  notice: null,
};

export default OperationEntityTable;
